import React, { useState, useEffect, useId, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { 
  FileText, 
  ArrowRight,
  CheckCircle2,
  Target,
  TrendingUp,
  Award,
  Zap,
  Sparkles,
  Download,
  ListChecks,
  Menu,
  X
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ResumeScanner from '../components/ResumeScanner';
import { trackEvent } from '@/utils/analytics';
import { CV_EDITOR_PATH } from '@/utils/resumeJourney';

const ATSChecklistSection = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const PREVIEW_ITEMS = [
    'Single-column layout — no tables or side-by-side columns',
    'Relevant skills and keywords from the job description',
    'Every bullet starts with a strong action verb',
    'Specific results and numbers you can support',
    'Standard section headers ATS systems recognise',
  ];

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    setLoading(true);
    try {
      // On Railway this uses the frontend's same-origin /api proxy; on other hosts an explicitly configured backend URL still works.
      const configuredBackendUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '');
      const apiBase = configuredBackendUrl ? `${configuredBackendUrl}/api` : '/api';
      const res = await fetch(`${apiBase}/resources/ats-checklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Download failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ResumeReady-ATS-Checklist-2026.pdf';
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setDownloaded(true);
      toast.success('Checklist downloaded.');
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const startChecklistSignup = () => {
    const destination = new URLSearchParams({ source: 'checklist', mode: 'signup', next: CV_EDITOR_PATH });
    const incoming = new URLSearchParams(window.location.search);
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'].forEach((key) => {
      const value = incoming.get(key);
      if (value) destination.set(key, value);
    });
    navigate(`/auth?${destination.toString()}`);
  };

  return (
    <section className="py-14 sm:py-18 px-4 sm:px-6 bg-gradient-to-b from-card/30 to-background">
      <div className="max-w-5xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Left: checklist preview */}
          <div>
            <Badge className="mb-4 bg-emerald-500/15 text-emerald-400 border-emerald-500/30">
              <Download className="w-3 h-3 mr-1" />Free Download — No Credit Card
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              The ATS Survival Checklist
            </h2>
            <p className="text-muted-foreground mb-5 leading-relaxed">
              A practical checklist for headings, wording and layout. Download it free.
            </p>
            <ul className="space-y-2.5 mb-6">
              {PREVIEW_ITEMS.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <div className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400" />
                  </div>
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
              <li className="flex items-start gap-2.5 text-sm text-muted-foreground/60 italic">
                <span className="ml-6">+26 more items across 5 sections…</span>
              </li>
            </ul>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <ListChecks className="w-4 h-4 text-emerald-400 shrink-0" />
              31 checklist items · PDF format · Print-ready · Updated 2026
            </div>
          </div>

          {/* Right: email capture */}
          <div className="bg-card border border-border rounded-2xl p-6 sm:p-8">
            {downloaded ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="font-bold text-lg mb-2">Checklist ready</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Use the checklist while building your CV.
                </p>
                <Button onClick={startChecklistSignup} className="w-full bg-primary hover:bg-blue-600" data-testid="post-download-signup-btn">
                  Build my CV
                </Button>
                <p className="text-[11px] text-muted-foreground mt-2">Free to build. No credit card needed.</p>
              </div>
            ) : (
              <>
                <h3 className="font-bold text-xl mb-1">Get the free PDF</h3>
                <p className="text-sm text-muted-foreground mb-5">
                  Enter your email and your download starts immediately.
                </p>
                <form onSubmit={handleDownload} className="space-y-3">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    data-testid="checklist-email-input"
                    className="h-11"
                  />
                  <Button type="submit" disabled={loading} className="w-full h-11" data-testid="checklist-download-btn">
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Preparing your PDF…
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Download className="w-4 h-4" />
                        Download Free Checklist
                      </span>
                    )}
                  </Button>
                </form>
                <p className="text-[11px] text-muted-foreground mt-3 text-center">
                  No spam. Unsubscribe any time. We'll also send you a free ATS scanner tip.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuId = useId();
  const menuButton = useRef(null);
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const dismiss = (event) => {
      if (event.key === 'Escape') { setMobileMenuOpen(false); menuButton.current?.focus(); }
    };
    document.addEventListener('keydown', dismiss);
    return () => document.removeEventListener('keydown', dismiss);
  }, [mobileMenuOpen]);
  const menuAction = (action) => { setMobileMenuOpen(false); action(); };

  const campaignProperties = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      landing_variant: 'homepage',
      page_path: window.location.pathname,
      utm_source: params.get('utm_source') || 'direct',
      utm_medium: params.get('utm_medium') || 'none',
      utm_campaign: params.get('utm_campaign') || 'none',
    };
  };

  const goToAuth = (mode, placement, next = '/dashboard') => {
    const destination = new URLSearchParams({
      source: `homepage_${placement}`,
      next,
    });
    if (mode === 'signup') destination.set('mode', 'signup');

    const incoming = new URLSearchParams(window.location.search);
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'].forEach((key) => {
      const value = incoming.get(key);
      if (value) destination.set(key, value);
    });

    trackEvent(mode === 'signup' ? 'signup_cta_click' : 'signin_cta_click', {
      ...campaignProperties(),
      cta_id: `homepage_${placement}`,
      destination: next,
    });
    navigate(`/auth?${destination.toString()}`);
  };

  const scrollToScanner = (placement) => {
    trackEvent('primary_cta_click', {
      ...campaignProperties(),
      cta_id: `homepage_${placement}`,
      destination: 'scanner',
    });
    document.getElementById('resume-scanner')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    trackEvent('landing_view', campaignProperties());
  }, []);

  useSEO({
    title: 'AI Resume Builder for Nigeria & Africa | ResumeReady',
    description: 'Build an ATS-ready resume with AI, tailor it to any job, practise your interview, and track every application — one platform for the whole job search.',
    canonical: 'https://becomeresumeready.com/',
    keywords: 'AI resume builder Nigeria, CV maker Nigeria, ATS resume checker, ATS score Nigeria, NYSC CV, tailor resume to job, mock interview AI Nigeria, cover letter generator Nigeria, free CV builder Africa',
    jsonLd: [
      {
        '@type': 'SoftwareApplication',
        '@id': 'https://becomeresumeready.com/#app',
        name: 'ResumeReady',
        applicationCategory: 'BusinessApplication',
        applicationSubCategory: 'ResumeBuilder',
        operatingSystem: 'Web',
        url: 'https://becomeresumeready.com/',
        description: 'AI-powered resume builder for Nigerian and African job seekers. Build ATS-optimised CVs, tailor applications to any job in one click, practise mock interviews with AI feedback, and track every application in one dashboard.',
        inLanguage: 'en',
        offers: {
          '@type': 'Offer',
          price: '5000',
          priceCurrency: 'NGN',
          description: 'Free ATS scan available. Paid plans start from ₦5,000/month.',
        },
        featureList: [
          'AI resume builder with ATS score checker',
          'One-click job tailoring',
          'AI mock interview with feedback',
          'AI cover letter generator',
          'Job application tracker',
          'Salary insights for Nigeria',
          'ATS checklist PDF download',
        ],
        audience: {
          '@type': 'Audience',
          audienceType: 'Nigerian and African job seekers, graduates, professionals, NYSC corps members',
          geographicArea: { '@type': 'Country', name: 'Nigeria' },
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is an ATS-compliant resume?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'ATS-friendly CVs use clear headings, readable formatting and relevant skills. ResumeReady highlights formatting and keyword issues. Employer requirements vary.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is ResumeReady free for Nigerian job seekers?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Scan a CV free, or create a free account to build one CV and download branded PDFs. Paid plans add AI tools and unbranded PDFs from ₦5,000 for 30 days, paid through Paystack.',
            },
          },
          {
            '@type': 'Question',
            name: 'How do I make my CV pass ATS in Nigeria?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Use clear headings, relevant job-description keywords and a readable layout. Review the exported file and follow the employer’s requested format. No CV format guarantees passing a screening system.',
            },
          },
          {
            '@type': 'Question',
            name: 'What CV format do Nigerian employers prefer?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Most Nigerian corporate employers (banks, telecoms, oil & gas, tech) prefer a clean single-column reverse-chronological CV, 1–2 pages, with a professional summary, quantified achievements, and a skills section. Government and public-sector applications often require a longer curriculum vitae format. ResumeReady provides templates for both, optimised for the Nigerian market.',
            },
          },
          {
            '@type': 'Question',
            name: 'Can ResumeReady help me get a job abroad from Nigeria (JAPA)?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. ResumeReady includes country-specific keyword guides for the UK, Canada, Germany, Australia, and the US — the most popular JAPA destinations. The one-click tailor feature rewrites your CV to match any international job description in seconds, and the mock interview tool helps you practise with questions common to each country.',
            },
          },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" aria-label="Main navigation">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3" data-testid="landing-header">
          <Link to="/" className="flex min-w-0 items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 sm:w-10 sm:h-10 shrink-0 rounded-lg bg-primary flex items-center justify-center">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <span className="text-lg sm:text-xl font-bold text-foreground whitespace-nowrap">ResumeReady™</span>
          </Link>
          
          <div className="hidden lg:flex items-center gap-4" data-testid="landing-desktop-navigation">
            {user ? (
              <Button 
                onClick={() => navigate('/dashboard')} 
                data-testid="dashboard-btn"
                className="bg-primary hover:bg-blue-600"
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/guides')}
                  data-testid="guides-btn"
                  className="text-muted-foreground hover:text-foreground hidden sm:inline-flex"
                >
                  Guides
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/about')}
                  data-testid="about-btn"
                  className="text-muted-foreground hover:text-foreground hidden md:inline-flex"
                >
                  About
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/pricing')}
                  data-testid="pricing-btn"
                  className="text-muted-foreground hover:text-foreground hidden sm:inline-flex"
                >
                  Pricing
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => goToAuth('login', 'navigation_signin')}
                  data-testid="sign-in-btn"
                  className="text-foreground"
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => goToAuth('signup', 'navigation_start', CV_EDITOR_PATH)}
                  data-testid="get-started-btn"
                  className="bg-primary hover:bg-blue-600"
                >
                  Build my CV
                </Button>
              </>
            )}
          </div>
          <button ref={menuButton} type="button" className="lg:hidden shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={mobileMenuOpen} aria-controls={menuId}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="landing-menu-toggle">
            {mobileMenuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
          {mobileMenuOpen && <div id={menuId} className="lg:hidden absolute left-4 right-4 top-full mt-1 grid gap-1 rounded-xl border border-border bg-card p-3 shadow-xl max-h-[calc(100dvh-6rem)] overflow-y-auto" data-testid="landing-mobile-menu">
            {user ? <Button onClick={() => menuAction(() => navigate('/dashboard'))}>Dashboard</Button> : <>
              <Button onClick={() => menuAction(() => goToAuth('signup', 'mobile_navigation_start', CV_EDITOR_PATH))}>Build my CV</Button>
              <Button variant="ghost" onClick={() => menuAction(() => goToAuth('login', 'mobile_navigation_signin'))}>Sign in</Button>
            </>}
            <Button variant="ghost" onClick={() => menuAction(() => navigate('/guides'))}>Guides</Button>
            <Button variant="ghost" onClick={() => menuAction(() => navigate('/pricing'))}>Pricing</Button>
            <Button variant="ghost" onClick={() => menuAction(() => navigate('/about'))}>About</Button>
          </div>}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 sm:pt-24 pb-10 sm:pb-14 px-4 sm:px-6 hero-gradient">
        <div className="max-w-5xl mx-auto text-center">
          {/* Platform-positioning eyebrow — bold brand signal, no fabricated stats */}
          <Badge className="mb-4 bg-primary/15 text-primary border-primary/30 px-3 py-1.5 text-xs sm:text-sm tracking-wider uppercase">
            <Sparkles className="w-3 h-3 mr-1.5" />
            CVs. Applications. Interview practice.
          </Badge>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 sm:mb-5 leading-tight">
            Build a CV<br/>
            <span className="gradient-text">you’re ready to send.</span>
          </h1>

          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto mb-4">
            Write your CV, tailor applications and practise interviews in one place.
          </p>

          {/* Short product journey; no hiring outcome promised. */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-[11px] sm:text-xs font-medium text-muted-foreground mb-6 sm:mb-8">
            <span className="px-2.5 py-1 rounded-full bg-card border border-border">Build</span>
            <span className="text-muted-foreground/50">→</span>
            <span className="px-2.5 py-1 rounded-full bg-card border border-border">Tailor</span>
            <span className="text-muted-foreground/50">→</span>
            <span className="px-2.5 py-1 rounded-full bg-card border border-border">Apply</span>
            <span className="text-muted-foreground/50">→</span>
            <span className="px-2.5 py-1 rounded-full bg-card border border-border">Interview</span>
            <span className="text-muted-foreground/50">→</span>
            <span className="px-2.5 py-1 rounded-full bg-primary/15 border border-primary/30 text-primary">Prepare</span>
          </div>

          <div className="flex flex-col items-center gap-3 mb-6 sm:mb-8">
            <Button 
              size="lg"
              onClick={() => user ? navigate(CV_EDITOR_PATH) : goToAuth('signup', 'hero_build', CV_EDITOR_PATH)}
              data-testid="hero-cta-btn"
              className="bg-primary hover:bg-blue-600 hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] text-base sm:text-lg px-8 sm:px-10 py-5 sm:py-6 group w-full sm:w-auto"
            >
              Build my CV
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <p className="text-xs text-muted-foreground">
              Start free. AI tools and unbranded PDFs require a paid plan.
            </p>
            <button
              onClick={() => scrollToScanner('hero_scan')}
              data-testid="hero-scan-btn"
              className="text-sm font-medium text-primary hover:text-primary/80 transition-colors underline underline-offset-4"
            >
              Already have a CV? Scan it free ↓
            </button>
            {!user && (
              <button
                onClick={() => goToAuth('login', 'hero_signin')}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <span className="underline underline-offset-2">Sign in</span>
              </button>
            )}
          </div>
          
          {/* Trust line */}
          <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-muted-foreground mb-8 sm:mb-10">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" /> ATS-optimised templates
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" /> Built for African &amp; global markets
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" /> Paid credits carry forward while active
            </span>
          </div>

          {/* Social proof stats */}
          <div className="flex flex-wrap justify-center gap-6 sm:gap-12 border-t border-border/40 pt-6 sm:pt-8">
            {[
              { number: '5', label: 'ATS-optimised templates' },
              { number: '8+', label: 'Job boards searched' },
              { number: 'Free', label: 'ATS scan · no card' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl sm:text-3xl font-extrabold text-foreground tabular-nums">{stat.number}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resume Scanner — Lead Magnet (No signup required) */}
      <section id="resume-scanner" className="py-12 sm:py-16 px-4 sm:px-6 bg-gradient-to-b from-background to-card/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-3 bg-green-500/15 text-green-500 border-green-500/30 px-3 py-1 text-xs tracking-wider uppercase">
              <Zap className="w-3 h-3 mr-1.5" /> Free Instant Score
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold mb-3">
              Check your CV’s <span className="gradient-text">format and keywords</span>
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
              Scan free. Sign up for the full report. AI rewrites require a paid plan.
            </p>
          </div>
          <ResumeScanner />
        </div>
      </section>

      {/* Product demonstration — evidence-led, no unsourced market statistics */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-card/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-3 bg-primary/15 text-primary border-primary/30">See the work before you commit</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Find the gap. Fix the evidence. Apply with confidence.</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">ResumeReady helps you work from your real experience — it does not invent achievements or guarantee interviews.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { step: '1', title: 'Scan your CV', description: 'Upload a PDF or DOCX to see formatting and ATS-readiness issues.' },
              { step: '2', title: 'See what needs work', description: 'Review missing sections, weak wording, and improvement opportunities.' },
              { step: '3', title: 'Build a clearer application', description: 'Use your genuine scope, actions, and outcomes to strengthen each bullet.' },
            ].map((item) => (
              <div key={item.step} className="bg-card border border-border rounded-2xl p-5 sm:p-6">
                <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold mb-4">{item.step}</div>
                <h3 className="font-bold text-base sm:text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ATS Checklist Lead Magnet */}
      <ATSChecklistSection />

      {/* Features */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Tools for your next application
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Build free. Use a paid plan for AI and career tools.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                icon: Zap,
                title: "AI Resume Writer",
                description: "Improve your summary and work descriptions.",
                color: "text-yellow-500"
              },
              {
                icon: Target,
                title: "Job Fit Analyzer",
                description: "Compare your CV with a job description.",
                color: "text-blue-500"
              },
              {
                icon: FileText,
                title: "One-Click Job Tailor",
                description: "Adapt your CV to a role. Review every change.",
                color: "text-pink-500"
              },
              {
                icon: FileText,
                title: "Cover Letter Generator",
                description: "Draft a letter for the role you want.",
                color: "text-green-500"
              },
              {
                icon: TrendingUp,
                title: "AI Mock Interview",
                description: "Practise by text or voice. Get feedback.",
                color: "text-purple-500"
              },
              {
                icon: TrendingUp,
                title: "Interview Prep",
                description: "Prepare answers to common questions.",
                color: "text-orange-500"
              },
              {
                icon: Award,
                title: "Professional Templates",
                description: "Choose a clean, readable layout.",
                color: "text-pink-500"
              }
            ].map((feature, idx) => (
              <div key={idx} className="bg-card border border-border rounded-2xl p-5 sm:p-6 hover:border-primary/50 transition-colors">
                <feature.icon className={`w-8 h-8 sm:w-10 sm:h-10 ${feature.color} mb-3 sm:mb-4`} />
                <h3 className="font-bold text-base sm:text-lg mb-2">{feature.title}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works — product-led, no fabricated quotes */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-card/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              How ResumeReady Works
            </h2>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              Start with your CV. Add tools when you need them.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              {
                n: '1',
                title: 'Build or import',
                desc: 'Enter your details or bring an existing CV.',
              },
              {
                n: '2',
                title: 'Improve your draft',
                desc: 'Type, dictate or request AI help. Keep only what fits.',
              },
              {
                n: '3',
                title: 'Match the role',
                desc: 'Compare your CV with the job requirements.',
              },
              {
                n: '4',
                title: 'Practice the interview',
                desc: 'Try a mock interview and review the feedback.',
              },
            ].map((step) => (
              <div key={step.n} className="bg-card border border-border rounded-2xl p-5 sm:p-6">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                  <span className="text-base font-bold text-primary">{step.n}</span>
                </div>
                <h3 className="font-bold text-base sm:text-lg mb-2">{step.title}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">{step.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground text-center mt-8 max-w-2xl mx-auto">
            ResumeReady helps you prepare. Jobs and interviews are not guaranteed.
          </p>
        </div>
      </section>

      {/* Product-first proof — testimonials are added only after consent and evidence are verified */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-card/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Your experience stays yours.</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">Use your real experience. Review suggestions. You choose what stays.</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-primary/20 via-blue-500/20 to-primary/20 rounded-3xl p-6 sm:p-12 border border-primary/30">
            <h2 className="text-xl sm:text-4xl font-bold mb-4">
              Ready to build your CV?
            </h2>
            <p className="text-sm sm:text-lg text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto">
              Start free. Add AI help when you need it.
            </p>
            
            <Button 
              size="lg"
              onClick={() => user ? navigate(CV_EDITOR_PATH) : goToAuth('signup', 'final_build', CV_EDITOR_PATH)}
              className="bg-primary hover:bg-blue-600 hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] text-base sm:text-lg px-8 sm:px-10 py-5 sm:py-6 group w-full sm:w-auto"
            >
              Build my CV
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs sm:text-sm">
              <button
                onClick={() => scrollToScanner('final_scan')}
                className="text-primary underline underline-offset-2"
              >
                Scan an existing CV
              </button>
              {!user && (
                <button
                  onClick={() => goToAuth('login', 'final_signin')}
                  className="text-muted-foreground hover:text-foreground underline underline-offset-2"
                >
                  Sign in to my account
                </button>
              )}
            </div>
            
            <p className="mt-4 text-xs sm:text-sm text-muted-foreground">
              Monthly plans non-refundable · 6-month plan partial refund on unused months · <a href="/terms" className="underline hover:text-primary">Full refund policy</a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              <span className="font-bold">ResumeReady™</span>
            </div>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm text-muted-foreground">
              <Link to="/about" className="hover:text-foreground">About</Link>
              <Link to="/pricing" className="hover:text-foreground">Pricing</Link>
              <Link to="/support" className="hover:text-foreground">Support</Link>
              <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground">Terms</Link>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              © 2026 ResumeReady. All rights reserved.
            </p>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-6" data-testid="company-attribution">
            ResumeReady is a product of <strong className="text-foreground">TrustMySkillApp Ltd.</strong>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
